import './stack-chart.scss';
import {Box, Typography, useTheme} from '@mui/material';
import {
  VictoryChart,
  VictoryArea,
  VictoryVoronoiContainer,
  VictoryTooltip,
  VictoryAxis,
  VictoryStack,
  DomainTuple,
  VictoryScatter,
  ForAxes,
} from 'victory';
import {useEffect, useState} from 'react';
import {ChartCard} from '../chart-card/chart-card';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../i18n/translations';
import {TimeLapseValue} from '../../../interfaces/time-lapse-value';
import {ErrorBox} from '../../error-box/error-box';

export interface StackChartDatum {
  x: string;
  y: number;
}

export interface StackData {
  id: string;
  label: string;
  color: string;
  values: Array<StackChartDatum>;
}

export type StackChartData = {
  data: Array<StackData>;
  // Note: We added this to fix the tooltip labels on the VictoryVoronoiContainer component.
  // Labels of points with the same coordinates would merge in the same tooltip. Thus, the tooltip
  // would have repeated information and was too large. With this information we are now able
  // to display the information in the tooltips as we want.
  coordinatesData: Array<{
    x: string;
    y: number;
    stacks: Array<{
      stackId: string;
      stackLabel: string;
    }>;
  }>;
};

/**
 * Stack chart used in the statistics dashboards:
 * - x: string
 * - y: number (with unit or not)
 */
export const StackChart = (props: {
  title: string;
  xLabel: string;
  yLabel: string;
  stackLabel: {
    singular: string;
    plural: string;
  };
  data: StackChartData;
  loading: boolean;
  handleRefresh: () => void;
  timeLapse?: {
    values: TimeLapseValue;
    handleChange: (values: TimeLapseValue) => void;
  };
  yUnitLabel?: string;
  subtitle?: string;
  yDomain?: ForAxes<DomainTuple>;
  error?: string;
}) => {
  // Hold state
  const [hoveredStack, setHoveredStack] = useState<string | undefined>();
  const [enabledStack, setEnabledStack] = useState<string | undefined>();
  const [data, setData] = useState<StackChartData['data']>(props.data.data);

  // Get theme
  const theme = useTheme();

  // Translations
  const {t} = useTranslation();

  // Update the data
  useEffect(() => {
    // Reset values
    setHoveredStack(undefined);
    setEnabledStack(undefined);

    // Set data
    setData(props.data.data);
  }, [props.data]);

  // Mouse over a legend
  const handleMouseOverLegend = (id: string) => {
    // Update the hovered stacked when the stack is not disabled, i.e., no stack
    // is enabled or the one that is enabled is the stack we are hovering.
    if (hoveredStack !== id && (!enabledStack || (enabledStack && id === enabledStack))) {
      setHoveredStack(id);
    }
  };

  // Mouse out of a legend
  const handleMouseOutLegend = (id: string) => {
    if (hoveredStack && hoveredStack === id) {
      setHoveredStack(undefined);
    }
  };

  // Click on legend
  const handleClickLegend = (id: string) => {
    if (enabledStack && enabledStack === id) {
      // Update
      setEnabledStack(undefined);

      // Update data
      setData(props.data.data);

      // Trigger mouse out
      handleMouseOutLegend(id);
    } else {
      // Update
      setEnabledStack(id);

      // Update data
      setData([...props.data.data].filter((obj) => obj.id === id));

      // Trigger mouse out
      handleMouseOutLegend(id);
    }
  };

  return (
    <ChartCard
      title={props.title}
      subtitle={props.subtitle}
      timeLapse={props.timeLapse}
      loading={props.loading}
      handleRefresh={props.handleRefresh}
    >
      <Box className="stack-chart-container">
        {props.data.data.length > 0 && !props.error && (
          <>
            <VictoryChart
              height={140}
              padding={{
                top: 16,
                bottom: 50,
                left: 50,
                right: 50,
              }}
              containerComponent={
                <VictoryVoronoiContainer
                  labels={({datum}) => {
                    // Get labels by looking for the coordinates and filter the stacks to only show the ones that are not disabled
                    const labelsData: Array<string> =
                      props.data.coordinatesData
                        .find((obj) => obj.x === datum.x && obj.y === datum.y)
                        ?.stacks.filter(
                          (stack) =>
                            (enabledStack && stack.stackId === enabledStack) || !enabledStack
                        )
                        .map((obj) => obj.stackLabel) || [];

                    // Return label
                    return labelsData.length !== 1
                      ? labelsData.findIndex((label) => label === datum.childName) !== 0
                        ? (null as any)
                        : `${props.stackLabel.plural}: ${labelsData.join(';\n ')}\n \n${
                            props.xLabel
                          }:
                                                ${datum.x}\n \n${props.yLabel}: ${datum.y?.toFixed(
                                                  props.yUnitLabel ? 2 : 0
                                                )} ${props.yUnitLabel || ''}`
                      : `${props.stackLabel.singular}: ${datum.childName}\n \n${props.xLabel}:
                                    ${datum.x}\n \n${props.yLabel}: ${datum.y?.toFixed(props.yUnitLabel ? 2 : 0)} ${
                                      props.yUnitLabel || ''
                                    }`;
                  }}
                  labelComponent={
                    <VictoryTooltip
                      constrainToVisibleArea
                      pointerLength={0}
                      flyoutStyle={{
                        stroke: theme.palette.divider,
                        fill: theme.palette.background.default,
                      }}
                    />
                  }
                />
              }
            >
              {/* Axis x */}
              <VictoryAxis
                label={props.xLabel}
                style={{
                  axis: {
                    stroke: theme.palette.divider,
                  },
                  tickLabels: {
                    fontSize: 6,
                    lineHeight: 1.57,
                    fontFamily: 'Roboto,sans-serif',
                    fill: theme.palette.text.primary,
                    angle: props.data.data[0].values.length > 6 ? -90 : 0,
                  },
                  axisLabel: {
                    padding: 32,
                    fontWeight: 'bold',
                    fontSize: 6,
                    lineHeight: 1.57,
                    fontFamily: 'Roboto,sans-serif',
                    fill: theme.palette.text.primary,
                  },
                  grid: {
                    stroke: theme.palette.divider,
                    strokeWidth: 1,
                  },
                }}
              />
              {/* Axis y */}
              <VictoryAxis
                dependentAxis
                fixLabelOverlap={true}
                label={props.yUnitLabel ? `${props.yLabel} (${props.yUnitLabel})` : props.yLabel}
                domain={props.yDomain}
                style={{
                  axis: {
                    stroke: theme.palette.divider,
                  },
                  tickLabels: {
                    fontSize: 6,
                    lineHeight: 1.57,
                    fontFamily: 'Roboto,sans-serif',
                    fill: theme.palette.text.primary,
                  },
                  axisLabel: {
                    padding: 32,
                    fontWeight: 'bold',
                    fontSize: 6,
                    lineHeight: 1.57,
                    fontFamily: 'Roboto,sans-serif',
                    fill: theme.palette.text.primary,
                  },
                  grid: {
                    stroke: theme.palette.divider,
                    strokeWidth: 1,
                  },
                }}
              />
              <VictoryStack>
                {data.map((obj) => {
                  return obj.values.length > 1 ? (
                    <VictoryArea
                      key={`stack-chart-area-${obj.id}`}
                      data={obj.values}
                      name={obj.label}
                      style={{
                        data: {
                          fill: obj.color,
                          fillOpacity: hoveredStack ? (hoveredStack === obj.id ? 0.8 : 0.2) : 0.6,
                          stroke: obj.color,
                          strokeOpacity: hoveredStack ? (hoveredStack === obj.id ? 1 : 0.2) : 1,
                          strokeWidth: 1,
                        },
                        labels: {
                          fontSize: 6,
                          fontFamily: 'Roboto,sans-serif',
                          fill: theme.palette.text.primary,
                        },
                      }}
                      events={[
                        {
                          target: 'data',
                          eventHandlers: {
                            onMouseOver: () => {
                              return [
                                {
                                  target: 'data',
                                  mutation: () => ({
                                    style: {
                                      fill: obj.color,
                                      fillOpacity: 0.8,
                                      stroke: obj.color,
                                      strokeOpacity: 1,
                                      strokeWidth: 1,
                                    },
                                  }),
                                },
                              ];
                            },
                            onMouseOut: () => {
                              return [{target: 'data', mutation: () => {}}];
                            },
                          },
                        },
                      ]}
                    />
                  ) : (
                    <VictoryScatter
                      key={`stack-chart-scatter-${obj.id}`}
                      data={obj.values}
                      name={obj.label}
                      style={{
                        data: {
                          fill: obj.color,
                          fillOpacity: hoveredStack ? (hoveredStack === obj.id ? 1 : 0.2) : 1,
                        },
                        labels: {
                          fontSize: 6,
                          fontFamily: 'Roboto,sans-serif',
                          fill: theme.palette.text.primary,
                        },
                      }}
                      size={2}
                    />
                  );
                })}
              </VictoryStack>
            </VictoryChart>
            <Box className="stack-chart-legend">
              {props.data.data.map((obj) => {
                return (
                  <Box
                    key={`stack-chart-legend-${obj.id}`}
                    className={`stack-chart-legend-row ${
                      enabledStack && enabledStack !== obj.id
                        ? 'stack-chart-legend-row-disabled'
                        : ''
                    }`}
                    onMouseOver={() => handleMouseOverLegend(obj.id)}
                    onMouseOut={() => handleMouseOutLegend(obj.id)}
                    onClick={() => handleClickLegend(obj.id)}
                  >
                    <Box
                      className="stack-chart-legend-dot"
                      sx={{
                        backgroundColor: obj.color,
                      }}
                    />
                    <Typography className="stack-chart-legend-text" variant="subtitle2">
                      {obj.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </>
        )}
        {props.data.data.length === 0 && !props.error && (
          <Typography variant="subtitle2" component="span" color="text.secondary">
            {t(translations.charts.no_data)}
          </Typography>
        )}
        {props.error && <ErrorBox error={props.error} />}
      </Box>
    </ChartCard>
  );
};
